import { IconAlertTriangle, IconFileSearch, IconLock } from "@tabler/icons-react";
import { Link } from "@tanstack/react-router";
import { Alert, Stack, Text } from "@/components/ui";

interface Props {
  solicitudId: string;
  enviadaAAutorizacion: boolean;
  /** Gestión Documental · fase 3 · la solicitud espera la revisión de sus documentos. */
  enRevisionDocumental: boolean;
  fueRechazada: boolean;
  /** La devolvió la revisión documental (tiene documentos observados), no un autorizador. */
  devueltaPorRevision: boolean;
  esSoloLectura: boolean;
  puedeAdjuntar: boolean;
  nivelDelRechazo: number | null;
  motivosRechazo: string[];
}

/**
 * Conjunto de banners contextuales que indican al usuario en qué
 * estado se encuentra la solicitud y qué puede o no hacer en esta
 * pantalla. Cada banner es excluyente del resto en la mayoría de
 * los casos, pero pueden coexistir en el render según la lógica.
 */
export function EstadoBanners({ solicitudId, enviadaAAutorizacion, enRevisionDocumental, fueRechazada, devueltaPorRevision, esSoloLectura, puedeAdjuntar, nivelDelRechazo, motivosRechazo }: Props) {
  return (
    <>
      {enRevisionDocumental && (
        <Alert variant="info" icon={<IconFileSearch size={16} stroke={1.7} />} title="En revisión documental">
          Los documentos de esta solicitud se están revisando antes de pasar a autorización. Si te observan alguno, lo reemplazas desde el{" "}
          <Link to="/solicitudes/$id/expediente" params={{ id: solicitudId }} className="font-medium text-multiva-600 hover:underline">
            expediente
          </Link>
          .
        </Alert>
      )}

      {enviadaAAutorizacion && (
        <Alert variant="warning" icon={<IconLock size={16} stroke={1.7} />} title="Solicitud en autorización">
          Esta solicitud ya fue enviada al autorizador. No puedes modificar sus archivos hasta que el autorizador la rechace · solo entonces volverás a poder agregar o eliminar documentos.
        </Alert>
      )}

      {fueRechazada && (
        <Alert
          variant="warning"
          icon={<IconAlertTriangle size={16} stroke={1.7} />}
          title={devueltaPorRevision ? "Solicitud devuelta por la revisión documental" : `Solicitud devuelta para corrección · nivel ${nivelDelRechazo}`}
        >
          <Stack gap="xs">
            <Text size="sm">
              {devueltaPorRevision
                ? "Se observaron documentos de la solicitud. Reemplaza los documentos observados y luego reenvíala · solo lo que reemplaces se vuelve a revisar."
                : `El autorizador rechazó la solicitud. Ajusta los archivos según los motivos y luego reenvíala · el flujo continuará desde el nivel ${nivelDelRechazo} (las autorizaciones previas se conservan · decisión 37).`}
            </Text>
            {motivosRechazo.length > 0 && (
              <div>
                <Text size="xs" fw={600} className="mb-1 mt-2">
                  {devueltaPorRevision ? "Observaciones:" : "Motivos del rechazo:"}
                </Text>
                <ul className="list-disc space-y-0.5 pl-5 text-sm">
                  {motivosRechazo.map((m, i) => (
                    <li key={i}>{m}</li>
                  ))}
                </ul>
              </div>
            )}
          </Stack>
        </Alert>
      )}

      {!puedeAdjuntar && !enviadaAAutorizacion && !enRevisionDocumental && !esSoloLectura && (
        <Alert variant="readonly" title="No puedes adjuntar en este estado">
          El estado actual no permite cargar documentos. Confirma primero el prorrateo.
        </Alert>
      )}

      {esSoloLectura && (
        <Alert variant="readonly" title="Vista de archivos">
          Modo consulta · puedes ver y descargar los archivos asociados a la solicitud. Solo el solicitante creador puede modificarlos.
        </Alert>
      )}
    </>
  );
}
